import type { DisciplineId, JobRoleDisciplineId, JobRoleId } from '../shared/ids';
import {
  FOUNDATION_SPLIT,
  STRUCTURAL_SPLIT,
  stageDisciplineNames,
} from '../takeoff/stage-registry';

/**
 * O que cada função executa.
 *
 * O vínculo que faltava para o sistema responder **quem faz** sem alguém digitar: o
 * cronograma diz que a Alvenaria do 3º está em execução nesta semana, e é este cadastro
 * que sabe que quem levanta alvenaria é pedreiro e servente — não o eletricista.
 *
 * É tabela de ligação, e não um campo `disciplineId` na função, porque a relação é
 * legitimamente de muitos para muitos: o pedreiro faz alvenaria **e** estrutura, e a
 * estrutura é feita por pedreiro, carpinteiro e armador. Um campo único obrigaria a
 * escolher uma das duas verdades.
 *
 * O vínculo é **global**, como a função (RN-013). A disciplina específica de uma obra
 * continua vinculável — o que se guarda é o par de identificadores, e uma disciplina de
 * obra só existe naquela obra.
 */
export interface JobRoleDiscipline {
  readonly id: JobRoleDisciplineId;
  readonly jobRoleId: JobRoleId;
  readonly disciplineId: DisciplineId;
  readonly createdAt: string;
}

/** Um par por função e disciplina — o mesmo vínculo não se cadastra duas vezes. */
export function jobRoleDisciplineNaturalKey(
  jobRoleId: JobRoleId,
  disciplineId: DisciplineId,
): string {
  return `${jobRoleId}|${disciplineId}`;
}

export function createJobRoleDiscipline(input: {
  id: JobRoleDisciplineId;
  jobRoleId: JobRoleId;
  disciplineId: DisciplineId;
  now: string;
}): JobRoleDiscipline {
  return {
    id: input.id,
    jobRoleId: input.jobRoleId,
    disciplineId: input.disciplineId,
    createdAt: input.now,
  };
}

/**
 * Índice de "quem faz o quê", montado uma vez por consulta.
 *
 * A alocação automática pergunta isto uma vez por recurso × atividade — trinta pessoas
 * por vinte atividades são seiscentas perguntas. Em `Set`, cada uma custa uma busca
 * direta; em varredura de lista, a proposta da semana viraria trabalho quadrático.
 */
export class JobRoleDisciplineIndex {
  private constructor(private readonly byRole: ReadonlyMap<string, ReadonlySet<string>>) {}

  static build(links: readonly JobRoleDiscipline[]): JobRoleDisciplineIndex {
    const map = new Map<string, Set<string>>();
    for (const link of links) {
      let disciplines = map.get(link.jobRoleId);
      if (disciplines === undefined) {
        disciplines = new Set();
        map.set(link.jobRoleId, disciplines);
      }
      disciplines.add(link.disciplineId);
    }
    return new JobRoleDisciplineIndex(map);
  }

  /**
   * A função executa esta disciplina?
   *
   * Função **sem** nenhum vínculo responde `false`, nunca "tudo". Tratar a ausência de
   * cadastro como permissão universal encheria a semana do pintor de horas de hidráulica
   * — e o planejador levaria um tempo até desconfiar de onde aquilo saiu. Sem vínculo, a
   * pessoa fica de fora da proposta e a tela diz por quê.
   */
  matches(jobRoleId: JobRoleId, disciplineId: DisciplineId): boolean {
    return this.byRole.get(jobRoleId)?.has(disciplineId) ?? false;
  }

  hasAny(jobRoleId: JobRoleId): boolean {
    return (this.byRole.get(jobRoleId)?.size ?? 0) > 0;
  }

  disciplinesOf(jobRoleId: JobRoleId): readonly DisciplineId[] {
    return [...(this.byRole.get(jobRoleId) ?? [])] as DisciplineId[];
  }
}

/**
 * Vínculos semeados no bootstrap — o ponto de partida, não a verdade final.
 *
 * São os pares que qualquer canteiro reconheceria, escritos por **nome** porque é assim
 * que o seed encontra função e disciplina já existentes. O escritório ajusta na tela de
 * mão de obra: obra que tem gesseiro fazendo alvenaria muda o vínculo, e o seed não
 * volta a escrever por cima (RN-110).
 *
 * **O par saiu do setor da folha**, que é onde a construtora já diz quem faz o quê:
 * carpintaria e armação são as duas frentes da estrutura, alvenaria é a dela, e o pessoal
 * do setor `GERAL` — betoneira e auxiliares — serve as três.
 *
 * Duas funções entram **sem disciplina**, e é resposta e não omissão: o operador de guincho
 * transporta, e o auxiliar de almoxarifado entrega. Nenhum dos dois executa uma frente, e
 * inventar-lhes uma faria a alocação automática propô-los para medir serviço que eles não
 * fazem.
 *
 * O pintor, que entrava vazio pelo mesmo motivo, ganhou as duas disciplinas dele: a Fase 21
 * trouxe `PINTURA INTERNA` e `PINTURA EXTERNA` no catálogo da construtora, e agora existe
 * onde pendurá-lo.
 *
 * **Fundação e Estrutural viraram peças na Fase 23**, e o par seguiu a troca: quem executava
 * a Estrutural executa PILARES, VIGAS, LAJE e ESCADAS; quem executava a Fundação, a fundação
 * e a contenção inteiras. A preliminar — topografia, ligações, canteiro — fica com quem a
 * folha não amarra a ofício: auxiliar, oficial de construção e supervisor. E o eletricista e
 * o encanador ganham as ligações provisórias, que são deles; o eletricista, também a LAJE,
 * onde a infraestrutura elétrica é embutida antes da concretagem.
 */
const ESTRUTURA = STRUCTURAL_SPLIT;
const FUNDACAO = FOUNDATION_SPLIT;
const PRELIMINAR = stageDisciplineNames('PRELIMINAR');

export const DEFAULT_ROLE_DISCIPLINES: Readonly<Record<string, readonly string[]>> = {
  'Auxiliar de Obra': [...PRELIMINAR, ...FUNDACAO, ...ESTRUTURA, 'Alvenaria'],
  'Auxiliar de Almoxarifado': [],
  'Assistente de Obra': [...FUNDACAO, ...ESTRUTURA, 'Alvenaria'],
  'Meio Oficial': [...ESTRUTURA, 'Alvenaria'],
  'Oficial de Alvenaria': ['Alvenaria'],
  // A carpintaria da obra é fôrma, e fôrma existe da sapata à laje.
  'Oficial de Carpintaria': [...FUNDACAO, ...ESTRUTURA],
  'Oficial de Armação': [...FUNDACAO, ...ESTRUTURA],
  // O genérico da folha: quem a construtora não amarra a um ofício só.
  'Oficial de Construção': [...PRELIMINAR, ...FUNDACAO, ...ESTRUTURA, 'Alvenaria'],
  'Operador de Betoneira': [...ESTRUTURA, 'Alvenaria'],
  'Operador de Guincho': [],
  Supervisor: [...PRELIMINAR, ...FUNDACAO, ...ESTRUTURA, 'Alvenaria'],
  Eletricista: ['Elétrica', 'LIGAÇÕES PROVISÓRIAS', 'LAJE'],
  Encanador: ['Hidráulica', 'LIGAÇÕES PROVISÓRIAS'],
  Pintor: ['PINTURA INTERNA', 'PINTURA EXTERNA'],
};
